import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  compose, entries, first, flatten, get, includes, map, max, multiply, range, reduce, size, times, unzip, values,
} from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import {
  Button, Dialog, DialogContent, DialogActions,
} from '../../common/components';
import { exportToExcel, exportIndividuals } from '../../common/utils';
import messages from '../messages';

const S = {
  ExportButton: styled.button`
    margin-right: 16px;
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    cursor: pointer;
    outline: none;
    :hover {
      text-decoration: underline;
    }
  `,
  Dialog: styled(Dialog)`
    .paper {
      width: 480px;
      padding-bottom: 24px;
    }
  `,
  DialogContent: styled(DialogContent)`
    padding-bottom: 40px;
  `,
  Paragraph: styled.p`
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Button: styled(Button)`
    min-height: 40px;
  `,
};

const ExportButton = React.memo(({
  location, newsletter, survey, stats, individuals,
}) => {
  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);

  const exportStats = useCallback(() => {
    if (size(stats.data)) {
      const data = {
        head: compose(
          map(num => (num % 2 ? '' : `${num / 2 + 1}번 문항`)),
          range(0),
          multiply(2),
          size,
          get('questions'),
        )(survey.data),
        body: compose(
          map(flatten),
          unzip,
          map((answer) => {
            const pairs = entries(answer);
            const rowLength = compose(
              max,
              map(size),
            )(stats.data);

            return size(pairs) < rowLength
              ? [...pairs, ...times(() => ['', ''], rowLength - size(pairs))]
              : pairs;
          }),
          reduce((acc, cur) => ({ ...acc, [cur + 1]: stats.data[cur + 1] || {} }), {}),
          range(0),
          size,
          first,
          values,
        )(survey.data),
      };

      const metadata = {
        fileName: `${get('title', newsletter.data)} 통계(${dayjs().format('YYYY년 MM월 DD일')}).xlsx`,
        sheetName: '통계',
      };

      exportToExcel(data, { metadata, merge: true });
    }
  }, [newsletter.data, stats.data, survey.data]);

  const handleClick = useCallback(() => {
    closeDialog();

    return (includes('individual', location.pathname)
      ? exportIndividuals(survey.data, individuals.data, {
        fileName: `${get('title', newsletter.data)} 개별 응답(${dayjs().format('YYYY년 MM월 DD일')}).xlsx`,
        sheetName: '개별 응답',
      })
      : exportStats()
    );
  }, [closeDialog, exportStats, survey.data, individuals.data, newsletter.data, location.pathname]);

  return (
    !newsletter.loading && (
      <>
        <S.ExportButton type="button" onClick={openDialog}>
          <FormattedMessage {...messages.exportButton} />
        </S.ExportButton>
        <S.Dialog open={open} onClose={closeDialog}>
          <S.DialogContent>
            <S.Paragraph>
              {`${includes('individual', location.pathname) ? '개별 응답을' : '통계를'} 액셀파일(.xlsx)로 내보내시겠습니까?`}
            </S.Paragraph>
          </S.DialogContent>
          <DialogActions>
            <S.Button
              type="button"
              variant="outlined"
              onClick={closeDialog}
            >
              취소
            </S.Button>
            <S.Button
              color="primary"
              variant="contained"
              onClick={handleClick}
            >
              저장
            </S.Button>
          </DialogActions>
        </S.Dialog>
      </>
    )
  );
});

ExportButton.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  newsletter: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
  }).isRequired,
  survey: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      questions: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  stats: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.shape({}),
  }).isRequired,
  individuals: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default withRouter(ExportButton);
